
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Camera, Image as ImageIcon, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { usePets } from '@/hooks/usePets';
import { createPost } from '@/services/postsService';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from '@/hooks/useSubscription';
import { canUploadMedia } from '@/services/subscriptionService';
import UpgradeModal from '@/components/UpgradeModal';
import StorageProgressBar from '@/components/StorageProgressBar';

const CreatePost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [selectedPetId, setSelectedPetId] = useState<string>('');
  const [privacySetting, setPrivacySetting] = useState<'public' | 'private' | 'followers_only'>('public');
  const { pets, isLoading: petsLoading } = usePets();
  const { subscription, quota } = useSubscription();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  // ✅ FASE 3: Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'storage'>('storage');
  
  // Check for pre-selected pet from navigation state
  useEffect(() => {
    const state = location.state as { selectedPetId?: string };
    if (state && state.selectedPetId) {
      setSelectedPetId(state.selectedPetId);
    }
  }, [location.state]);

  const handleFileChange = (files: FileList | null) => {
    console.log("File change detected:", files);
    if (!files || files.length === 0) {
      setFiles(null);
      setPreview(null);
      return;
    }

    setFiles(files);
    
    const file = files[0];
    const fileUrl = URL.createObjectURL(file);
    setPreview(fileUrl);
    
    return () => {
      URL.revokeObjectURL(fileUrl);
    };
  };

  const handleUploadAreaClick = () => {
    console.log("Upload area clicked");
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Native camera functions
  const takePhoto = async () => {
    try {
      const image = await CapCamera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera
      });
      
      const response = await fetch(image.webPath!);
      const blob = await response.blob();
      const file = new File([blob], `photo_${Date.now()}.${image.format}`, {
        type: `image/${image.format}`
      });
      
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      handleFileChange(dataTransfer.files);
      
      toast.success(t('modals.post.photoTaken'));
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Error taking photo:', error);
        toast.error(t('modals.post.photoError'));
      }
    }
  };

  const pickFromGallery = async () => {
    try {
      const images = await CapCamera.pickImages({
        quality: 90,
        limit: 10 // Allow multiple selection
      });
      
      if (!images.photos || images.photos.length === 0) {
        return;
      }

      const dataTransfer = new DataTransfer();
      
      for (const photo of images.photos) {
        const response = await fetch(photo.webPath);
        const blob = await response.blob();
        const file = new File([blob], `gallery_${Date.now()}_${Math.random()}.${photo.format}`, {
          type: `image/${photo.format}`
        });
        dataTransfer.items.add(file);
      }
      
      handleFileChange(dataTransfer.files);
      toast.success(t('modals.post.gallerySelected', { count: images.photos.length }));
    } catch (error: any) {
      if (error.message !== 'User cancelled photos app') {
        console.error('Error picking from gallery:', error);
      toast.error(t('modals.post.galleryError'));
      }
    }
  };

  const recordVideo = () => {
    if (videoInputRef.current) {
      videoInputRef.current.click();
    }
  };

  const isNativePlatform = Capacitor.isNativePlatform();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!files || files.length === 0) {
      toast.error(t('modals.post.pleaseSelectFile'));
      return;
    }
    
    if (!selectedPetId) {
      toast.error(t('modals.post.pleaseSelectPet'));
      return;
    }
    
    // ✅ FASE 3: Check storage limit before uploading
    const file = files[0];
    const storageCheck = await canUploadMedia(file.size);
    if (!storageCheck.canUpload) {
      console.log('🚫 Storage limit reached, showing upgrade modal');
      setUpgradeReason('storage');
      setShowUpgradeModal(true);
      return;
    }

    setIsUploading(true);
    
    try {
      console.log("Submitting form with files:", files);
      const mediaFiles = Array.from(files);
      
      const formData: {
        petId: string;
        caption?: string;
        mediaFiles: File[];
        privacySetting: 'public' | 'private' | 'followers_only';
      } = {
        petId: selectedPetId,
        mediaFiles,
        privacySetting,
      };
      
      if (caption) {
        formData.caption = caption;
      }
      
      console.log("Calling createPost with:", formData);
      const result = await createPost(formData);
      
      toast.success(t('modals.post.memoryPostedSuccess'));
      
      // Navigate back to the pet profile or to the feed
      if (location.state?.selectedPetId) {
        navigate(`/pet/${selectedPetId}`);
      } else {
        navigate('/feed');
      }
    } catch (error: any) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const selectedPet = pets.find(p => p.id === selectedPetId);

  return (
    <div className="p-4 pb-24">
      {/* ✅ FASE 3: Storage Progress Bar - always visible */}
      {quota && (
        <div className="mb-6">
          <StorageProgressBar
            used={quota.storage.used}
            total={quota.storage.total}
            showUpgradeButton={true}
            compact={false}
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="pet-select">{t('modals.post.selectPet')}</Label>
          <Select
            value={selectedPetId}
            onValueChange={setSelectedPetId}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('modals.post.choosePet')} />
            </SelectTrigger>
            <SelectContent>
              {petsLoading ? (
                <SelectItem value="loading" disabled>
                  {t('modals.post.loadingPets')}
                </SelectItem>
              ) : pets.length > 0 ? (
                pets.map((pet) => (
                  <SelectItem key={pet.id} value={pet.id} className="flex items-center">
                    <div className="flex items-center">
                      <Avatar className="h-6 w-6 mr-2">
                        {pet.photo_url ? (
                          <AvatarImage src={pet.photo_url} alt={pet.name} />
                        ) : (
                          <AvatarFallback>{pet.name.charAt(0)}</AvatarFallback>
                        )}
                      </Avatar>
                      {pet.name}
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-pets" disabled>
                  {t('modals.post.noPetsAvailable')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          
          {pets.length === 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {t('modals.post.addPetFirst')}{' '}
              <Button
                variant="link"
                className="p-0 h-auto text-blue-600"
                onClick={() => navigate('/my-pets')}
              >
                {t('modals.post.addPet')}
              </Button>
            </p>
          )}

          {selectedPet && (
            <div className="flex items-center mt-2">
              <Avatar className="h-8 w-8 mr-2">
                {selectedPet.photo_url ? (
                  <AvatarImage src={selectedPet.photo_url} alt={selectedPet.name} />
                ) : (
                  <AvatarFallback>{selectedPet.name.charAt(0)}</AvatarFallback>
                )}
              </Avatar>
              <span className="text-sm font-medium">{selectedPet.name}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="media-upload">{t('modals.post.photoVideo')}</Label>
          
          <div className="flex flex-col items-center">
            {preview ? (
              <div className="relative mb-4 w-full max-h-80 rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    setFiles(null);
                    setPreview(null);
                  }}
                >
                  {t('modals.post.remove')}
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 w-full mb-4 flex flex-col items-center">
                {isNativePlatform ? (
                  <>
                    <div className="flex flex-wrap gap-3 mb-4 justify-center">
                      <Button
                        type="button"
                        onClick={takePhoto}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Camera className="h-5 w-5" />
                        {t('modals.post.takePhoto')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={recordVideo}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Video className="h-5 w-5" />
                        {t('modals.post.takeVideo')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={pickFromGallery}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <ImageIcon className="h-5 w-5" />
                        {t('modals.post.pickFromGallery')}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={handleUploadAreaClick}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Upload className="h-5 w-5" />
                        {t('modals.post.selectFile')}
                      </Button>
                    </div>
                    
                    <p className="text-gray-500 text-sm text-center">
                      {t('modals.post.orSelectFile')}
                    </p>
                  </>
                ) : (
                  <>
                    <div 
                      className="cursor-pointer"
                      onClick={handleUploadAreaClick}
                    >
                      <Camera className="h-12 w-12 text-gray-400 mb-2" />
                    </div>
                    <p className="text-gray-500 text-sm text-center">
                      {t('modals.post.uploadPrompt')}
                    </p>
                  </>
                )}
              </div>
            )}

            <input
              id="media-upload"
              type="file"
              className="hidden"
              accept="image/*,video/*"
              multiple={true}
              onChange={(e) => handleFileChange(e.target.files)}
              ref={fileInputRef}
            />
            
            <input
              id="video-capture"
              type="file"
              className="hidden"
              accept="video/*"
              capture="environment"
              onChange={(e) => handleFileChange(e.target.files)}
              ref={videoInputRef}
            />
            
            <p className="text-xs text-gray-500">
              {t('modals.post.uploadDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="caption">{t('modals.post.caption')}</Label>
          <Textarea
            id="caption"
            placeholder={t('modals.post.captionPlaceholder')}
            className="h-24"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('modals.post.privacy')}</Label>
          <RadioGroup
            value={privacySetting}
            onValueChange={(value: 'public' | 'private' | 'followers_only') =>
              setPrivacySetting(value)
            }
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="public" id="privacy-public" />
              <Label htmlFor="privacy-public">{t('modals.post.privacyPublic')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="followers_only" id="privacy-followers" />
              <Label htmlFor="privacy-followers">{t('modals.post.privacyFollowersOnly')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="private" id="privacy-private" />
              <Label htmlFor="privacy-private">{t('modals.post.privacyPrivate')}</Label>
            </div>
          </RadioGroup>
        </div>

        <Button
          type="submit"
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={isUploading || !files || !selectedPetId}
        >
          {isUploading ? (
            <>
              <Upload className="mr-2 h-4 w-4 animate-spin" />
              {t('modals.post.uploading')}
            </>
          ) : (
            t('modals.post.post')
          )}
        </Button>
      </form>
      
      {/* ✅ FASE 3: Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        currentTier={subscription?.data?.subscription_tier || 'free'}
      />
    </div>
  );
};

export default CreatePost;
